import { AlertCircle, BookmarkCheck } from "lucide-react";
import { useMemo } from "react";

import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";

import { useBookmarks } from "./use-bookmarks";

/**
 * The Me tab's view of the attendee's own bookmarks: how many sessions they
 * have saved, and a button that opens the Agenda on its "My schedule" filter.
 *
 * The count is the number of distinct sessions, the same figure the filter
 * chip shows. While the bookmarks load the card has its title and nothing
 * else. When they cannot be read it says so in a sentence and offers no
 * button, because the filter is disabled on the agenda in that case too. With
 * none saved there is no button either: it would open an empty list, where
 * nothing can be bookmarked; the sentence points at the bookmark on a session
 * instead, and the Agenda tab is one tap away.
 */
export function MyScheduleCard({
  uid,
  eventId,
  onOpen,
}: {
  uid: string;
  eventId: string;
  /** Opens the Agenda with "My schedule" selected. */
  onOpen: () => void;
}) {
  const { state } = useBookmarks(uid, eventId);
  const count = useMemo(
    () =>
      state.status === "success"
        ? new Set(state.data.map((bookmark) => bookmark.agendaItemId)).size
        : null,
    [state],
  );

  return (
    <Card data-testid="my-schedule">
      <CardContent className="space-y-3 py-4">
        <p className="flex items-center gap-2 font-medium text-foreground">
          <BookmarkCheck
            className="h-5 w-5 shrink-0 text-muted-foreground"
            aria-hidden
          />
          My schedule
        </p>
        {state.status === "error" && (
          <p
            className="flex items-start gap-2 text-sm text-muted-foreground"
            data-testid="my-schedule-error"
          >
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span className="min-w-0">
              Your schedule could not be loaded. Reload the page to try again.
            </span>
          </p>
        )}
        {count === 0 && (
          <p
            className="text-sm text-muted-foreground"
            data-testid="my-schedule-count"
          >
            You have not bookmarked any sessions yet. Tap the bookmark on a
            session to add it.
          </p>
        )}
        {count !== null && count > 0 && (
          <>
            <p
              className="text-sm text-muted-foreground"
              data-testid="my-schedule-count"
            >
              {`You have bookmarked ${count} ${count === 1 ? "session" : "sessions"}.`}
            </p>
            <Button
              variant="outline"
              className="h-11 w-full sm:w-auto"
              onClick={onOpen}
              data-testid="my-schedule-open"
            >
              Open my schedule
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
